import { useEffect, useState } from "react";
import { Avatar, Button, Modal, SegmentedControl, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { activeWorkspaceHeader, fileUrl } from "../lib/transport.js";

/* ─────────────── shared feed types, kept here so the parts can use them ─────────────── */

export type PostTeam = { id: string; name: string };
export type PostImageRef = { id: string; fileName: string; contentType: string };
export type TeamOption = { id: string; name: string; memberCount: number; isMember: boolean };

/**
 * An image attached to a post.
 *
 * Fetched with the office header and shown from a blob URL rather than put in
 * an `<img src>`: a bare image request cannot carry `x-workspace-id`, and a
 * post in a non-default office would 404.
 */
export function PostImage({ image }: { image: PostImageRef }) {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;
    fetch(fileUrl(`/api/v1/files/${image.id}/preview`), { credentials: "include", headers: activeWorkspaceHeader() })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(String(res.status)))))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [image.id]);

  if (failed) return <div className="feed-image feed-image--missing">Image unavailable</div>;
  if (!src) return <div className="feed-image feed-image--loading" aria-hidden="true" />;
  return <img className="feed-image" src={src} alt={image.fileName} loading="lazy" />;
}

/** Team targeting: no selection means the whole office. */
export function TeamPicker({
  teams,
  value,
  onChange,
}: {
  teams: TeamOption[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  if (teams.length === 0) return null;
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="feed-teams" role="group" aria-label="Who can see this">
      <button
        type="button"
        className={`feed-team-chip ${value.length === 0 ? "is-on" : ""}`}
        aria-pressed={value.length === 0}
        onClick={() => onChange([])}
      >
        Whole office
      </button>
      {teams.map((team) => (
        <button
          key={team.id}
          type="button"
          className={`feed-team-chip ${value.includes(team.id) ? "is-on" : ""}`}
          aria-pressed={value.includes(team.id)}
          onClick={() => toggle(team.id)}
        >
          {team.name}
        </button>
      ))}
    </div>
  );
}

type AckPerson = { id: string; displayName: string; avatarUrl: string | null; ackedAt: string | null };
type AckPage = { people: AckPerson[]; total: number; hasMore: boolean };
const ACK_PAGE = 25;

/** Who has and has not acknowledged an announcement. Author and admins only. */
export function AckListModal({ postId, onClose }: { postId: string | null; onClose: () => void }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"pending" | "acknowledged">("pending");
  const [people, setPeople] = useState<AckPerson[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPage = (offset: number) => {
    if (!postId) return Promise.resolve();
    setLoading(true);
    return api
      .get<AckPage>(`/posts/${postId}/acknowledgements?status=${tab}&limit=${ACK_PAGE}&offset=${offset}`)
      .then((res) => {
        setPeople((prev) => (offset === 0 ? res.people : [...prev, ...res.people]));
        setTotal(res.total);
        setHasMore(res.hasMore);
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load acknowledgements.", { tone: "danger" });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setPeople([]);
    setTotal(0);
    setHasMore(false);
    void fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, tab]);

  return (
    <Modal open={!!postId} onClose={onClose} title="Acknowledgements">
      <SegmentedControl
        aria-label="Acknowledgement status"
        value={tab}
        onChange={setTab}
        options={[
          { value: "pending", label: "Not yet" },
          { value: "acknowledged", label: "Acknowledged" },
        ]}
      />
      <p className="feed-ack-total">{total} {total === 1 ? "person" : "people"}</p>
      {loading && people.length === 0 ? (
        <SkeletonText lines={3} />
      ) : people.length === 0 ? (
        <p className="feed-ack-empty">{tab === "pending" ? "Everyone has acknowledged this." : "Nobody has acknowledged this yet."}</p>
      ) : (
        <ul className="feed-ack-list">
          {people.map((person) => (
            <li key={person.id}>
              <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={24} />
              <span>{person.displayName}</span>
              {person.ackedAt && (
                <time dateTime={person.ackedAt}>{new Date(person.ackedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</time>
              )}
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <Button variant="secondary" size="sm" loading={loading} onClick={() => void fetchPage(people.length)}>
          Load more
        </Button>
      )}
    </Modal>
  );
}
